import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import { Calculator, WifiOff, CloudOff, Cloud, Info, Plus, FilePlus2, Trash2, AlertTriangle, UserCog } from 'lucide-react';
import { getLocalSimulations, syncAllPendingData } from '../../services/offlineService';
import { getToken } from '../../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;

// V38.01 — Champs du profil membre exploités par le préremplissage du
// simulateur : s'ils sont absents du profil, ils ne peuvent pas être
// prefillés → listés dans le bandeau « Compléter le profil ».
const PROFILE_PREFILL_FIELDS = [
  { key: 'chiffre_affaires', label: "Chiffre d'affaires" },
  { key: 'effectif', label: 'Effectif' },
  { key: 'date_creation_entreprise', label: 'Date de création (ancienneté)', alt: ['date_creation'] },
  { key: 'experience_professionnelle_annees', label: 'Expérience professionnelle', alt: ['experience_pro_annees', 'experience_annees', 'experience_pro'] },
  { key: 'territoire', label: 'Territoire' },
];

export default function HeaderSection({
  isOnline, localSimulations, showLocalSimulations, setShowLocalSimulations,
  getDashboardLabel, getDashboardPath,
  modeSimulateur, setModeSimulateur,
  membreData, unsyncedCount,
  deleteLocalSimulation, setLocalSimulations,
  handleResetAvenant,
  handleLoadLocalSimulation,
  onCompleteProfil,
}) {
  const missingProfileFields = membreData
    ? PROFILE_PREFILL_FIELDS.filter((f) => {
        const values = [membreData[f.key], ...(f.alt || []).map((k) => membreData[k])];
        return !values.some((v) => v !== undefined && v !== null && String(v).trim() !== '');
      })
    : [];

  return (
    <>
      {/* Header institutionnel — Mutuelle Premium */}
      <div className="relative bg-gradient-to-br from-[#0a1e3f] via-[#0B3C5D] to-[#0a1e3f] text-white shadow-2xl overflow-hidden border-b border-amber-300/15">
        {/* Motif décoratif feutré */}
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-y-32 translate-x-32 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-amber-200 rounded-full blur-3xl translate-y-24 -translate-x-24 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <BackButton variant="dark" label={`Retour ${getDashboardLabel()}`} fallback={getDashboardPath()} />
            <div className="flex items-center gap-3">
              {/* Indicateur mode hors ligne */}
              {!isOnline && (
                <Badge className="bg-amber-600 text-white border-0 px-3 py-1.5 text-xs font-semibold shadow-lg animate-pulse">
                  <WifiOff className="h-3 w-3 mr-1.5" />
                  Mode hors ligne
                </Badge>
              )}
              {/* Bouton simulations locales */}
              {localSimulations.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowLocalSimulations(!showLocalSimulations)}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  <CloudOff className="h-3.5 w-3.5 mr-1.5" />
                  {localSimulations.length} local
                </Button>
              )}
              {/* Badge statut connexion */}
              <Badge className={`${isOnline ? 'bg-emerald-700/90 border border-emerald-400/30' : 'bg-amber-600'} text-white px-3 py-1.5 text-xs font-semibold shadow-lg`}>
                {isOnline ? '✓ En ligne' : '⚠ Hors ligne'}
              </Badge>
            </div>
          </div>

          {/* Filet officiel doré */}
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/85 mb-2">
            <span className="inline-block w-7 h-px bg-amber-300/80" />
            <span className="font-semibold">COPPAM</span>
            <span className="text-white/40">|</span>
            <span>Garanties Solidaires</span>
            <span className="text-white/40">|</span>
            <span>Calcul officiel certifié</span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Sceau institutionnel */}
            <div className="relative hidden sm:block flex-shrink-0">
              <div className="absolute inset-0 bg-amber-300/20 rounded-xl blur-xl"></div>
              <div className="relative inline-flex items-center justify-center w-12 h-12 bg-white/12 backdrop-blur-md text-white rounded-xl shadow-lg border border-white/25">
                <Calculator className="h-6 w-6 drop-shadow-lg" />
              </div>
            </div>
            
            {/* Titre institutionnel */}
            <div className="flex-1 min-w-0">
              <h1 className="text-[clamp(1.05rem,1.9vw,1.4rem)] leading-[1.1] font-black tracking-tight text-white drop-shadow-lg" style={{ fontFamily: '"Playfair Display", "Georgia", serif' }}>
                SIMULATEUR CADRE <span className="text-amber-200 font-bold">RCDS&nbsp;BATICO</span>
              </h1>
              <p className="text-[12px] md:text-[12.5px] text-white/90 mt-1 font-medium max-w-3xl">
                Responsabilité civile et décennale — Articles 1792 et suivants du Code civil.
              </p>
              <div className="mt-1 space-y-0.5 text-[10px] text-white/65 leading-snug max-w-3xl">
                <div>Cadre coopératif — Loi n° 47-1775 du 10 septembre 1947 portant statut de la coopération.</div>
                <div>Économie sociale et solidaire — Loi n° 2014-856 du 31 juillet 2014 relative à l'ESS.</div>
              </div>
            </div>
            
            {/* Badges institutionnels — foncés, lisibles */}
            <div className="hidden lg:flex flex-col gap-1.5 items-end flex-shrink-0">
              <Badge className="text-[10px] px-2.5 py-1 font-semibold border-0 bg-amber-500 text-white shadow-md hover:bg-amber-600">
                <Calculator className="h-3 w-3 mr-1" /> Calcul instantané
              </Badge>
              <div className="flex gap-1.5">
                <Badge className="text-[10px] px-2 py-0.5 font-semibold border-0 bg-blue-700 text-white shadow-sm hover:bg-blue-800">Transparent</Badge>
                <Badge className="text-[10px] px-2 py-0.5 font-semibold border-0 bg-indigo-700 text-white shadow-sm hover:bg-indigo-800">Coopératif</Badge>
              </div>
            </div>
          </div>

          {/* Filet inférieur fin doré */}
          <div className="mt-3 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
        </div>
        
        {/* Vague décorative compacte */}
        <div className="relative">
          <svg className="w-full h-3 fill-slate-50" viewBox="0 0 1200 30" preserveAspectRatio="none">
            <path d="M0,15 Q150,5 300,15 T600,15 T900,15 T1200,15 L1200,30 L0,30 Z" className="drop-shadow-lg"></path>
          </svg>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-2">

        {/* Panneau des simulations locales (Mode hors ligne) */}
        {showLocalSimulations && localSimulations.length > 0 && (
          <div className="mb-6">
            <Card className="shadow-xl border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 rounded-xl">
                      <CloudOff className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-amber-900">Simulations sauvegardées localement</CardTitle>
                      <CardDescription className="text-amber-700">
                        {localSimulations.length} simulation(s) • Cliquez pour charger
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowLocalSimulations(false)}
                    className="text-amber-700 hover:bg-amber-100"
                  >
                    Fermer
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {localSimulations.map((sim) => (
                    <div
                      key={sim.id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
                    >
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => handleLoadLocalSimulation(sim)}
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {sim.mode === 'annuel' ? 'Annuel' : 'Nominatif'}
                          </Badge>
                          <span className="font-medium text-gray-800">
                            CA: {parseFloat(sim.formData?.ca_ht || 0).toLocaleString('fr-FR')} €
                          </span>
                          {sim.resultat?.offline_estimation && (
                            <Badge className="bg-amber-600 text-white text-xs font-semibold">
                              Estimation
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(sim.created_at).toLocaleString('fr-FR')}
                          {!sim.synced && (
                            <span className="ml-2 text-amber-600">• Non synchronisé</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleLoadLocalSimulation(sim)}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          Charger
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteLocalSimulation(sim.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {isOnline && localSimulations.some(s => !s.synced) && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <Button
                      size="sm"
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={async () => {
                        const token = getToken();
                        if (token) {
                          const result = await syncAllPendingData(API, token);
                          if (result.success) {
                            toast.success('Synchronisation terminée');
                            const updated = await getLocalSimulations('batico');
                            setLocalSimulations(updated);
                          }
                        }
                      }}
                    >
                      <Cloud className="h-4 w-4 mr-2" />
                      Synchroniser tout
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bandeau mode hors ligne actif */}
        {!isOnline && (
          <div className="mb-4">
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-amber-500 rounded-lg animate-pulse">
                    <WifiOff className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">Mode hors ligne activé</h4>
                    <p className="text-sm text-amber-700">
                      Vos simulations seront sauvegardées localement et synchronisées automatiquement à la reconnexion.
                    </p>
                  </div>
                  <Badge className="bg-amber-600 text-white font-semibold">
                    {unsyncedCount > 0 ? `${unsyncedCount} en attente` : 'Prêt'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bandeau données membre pré-remplies */}
        {membreData && (
          <div className="mb-4">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Info className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Simulation pour {membreData.prenom} {membreData.nom}</h4>
                    <p className="text-sm text-blue-700">
                      {membreData.raison_sociale && `${membreData.raison_sociale} • `}
                      {membreData.siret && `SIRET: ${membreData.siret} • `}
                      {membreData.territoire}
                    </p>
                  </div>
                  <Badge className="bg-blue-600 text-white font-semibold">Données pré-remplies</Badge>
                </div>
                {/* V38.01 — Champs absents du profil membre (non préremplis) */}
                {missingProfileFields.length > 0 && (
                  <div
                    className="mt-3 flex items-center gap-3 flex-wrap rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
                    data-testid="missing-profile-fields-banner"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-800 flex-1 min-w-[220px]">
                      <strong>Non préremplis (absents du profil) :</strong>{' '}
                      {missingProfileFields.map((f) => f.label).join(', ')}
                    </p>
                    {onCompleteProfil && membreData.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={onCompleteProfil}
                        className="border-amber-400 text-amber-800 hover:bg-amber-100"
                        data-testid="complete-profil-btn"
                      >
                        <UserCog className="h-4 w-4 mr-1.5" /> Compléter le profil
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Toggle Principal : Nouvelle Souscription / Avenant */}
        <div className="mb-8">
          <Card className="shadow-xl border-0 ring-2 ring-blue-100/50 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl">
                    <Calculator className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">Mode du Simulateur</h3>
                    <p className="text-sm text-slate-500">Sélectionnez le type d'opération à effectuer</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bouton Nouvelle Souscription */}
                <button
                  onClick={() => {
                    setModeSimulateur('souscription');
                    handleResetAvenant();
                  }}
                  className={`p-5 rounded-xl border-2 transition-all duration-300 text-left ${
                    modeSimulateur === 'souscription'
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 ring-2 ring-blue-200 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${
                      modeSimulateur === 'souscription' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      <Plus className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className={`font-bold text-lg ${modeSimulateur === 'souscription' ? 'text-blue-700' : 'text-slate-700'}`}>
                        Nouvelle Souscription
                      </div>
                      <p className={`text-sm mt-1 ${modeSimulateur === 'souscription' ? 'text-blue-500' : 'text-slate-400'}`}>
                        Créer une nouvelle souscription RCD Solidaire BATICO
                      </p>
                      {modeSimulateur === 'souscription' && (
                        <Badge className="mt-2 bg-blue-600 text-white font-semibold">Mode actif</Badge>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Bouton Créer un Avenant */}
                <button
                  onClick={() => setModeSimulateur('avenant')}
                  className={`p-5 rounded-xl border-2 transition-all duration-300 text-left ${
                    modeSimulateur === 'avenant'
                      ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 ring-2 ring-purple-200 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${
                      modeSimulateur === 'avenant' 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      <FilePlus2 className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className={`font-bold text-lg ${modeSimulateur === 'avenant' ? 'text-purple-700' : 'text-slate-700'}`}>
                        Créer un Avenant
                      </div>
                      <p className={`text-sm mt-1 ${modeSimulateur === 'avenant' ? 'text-purple-500' : 'text-slate-400'}`}>
                        Ajouter des activités à un contrat existant
                      </p>
                      {modeSimulateur === 'avenant' && (
                        <Badge className="mt-2 bg-purple-600 text-white font-semibold">Mode actif</Badge>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
